import { useCallback, useState } from 'react';
import type { ContentProfile, WeekPlan } from '@content-planner/shared';
import { api, ApiClientError } from '../lib/api.js';

type Status = 'idle' | 'loading' | 'error';

type State = {
  weeks: WeekPlan[] | null;
  status: Status;
  error: string | null;
};

const INITIAL: State = { weeks: null, status: 'idle', error: null };

/**
 * Generates the whole month in a single call. The full plan lands in `weeks`;
 * the page shows Week 1 and gates the rest behind lead capture (the data is
 * already in hand, so no second call is needed).
 */
export function useGenerateMonth() {
  const [state, setState] = useState<State>(INITIAL);

  const generate = useCallback(
    async (args: { profile: ContentProfile; month: string }) => {
      setState((s) => ({ ...s, status: 'loading', error: null }));
      try {
        const res = await api.generateMonth({ profile: args.profile, month: args.month });
        setState({ weeks: res.weeks, status: 'idle', error: null });
        return { ok: true as const, weeks: res.weeks };
      } catch (err) {
        const message =
          err instanceof ApiClientError
            ? err.message
            : 'We could not reach the planner. Check your connection and try again.';
        setState((s) => ({ ...s, status: 'error', error: message }));
        return { ok: false as const, message };
      }
    },
    [],
  );

  const reset = useCallback(() => setState(INITIAL), []);

  return { ...state, generate, reset };
}
